import { useQuery } from '@tanstack/react-query';
import { useId, useState } from 'react';
import Menu from 'storybookConfig/stories/cells/Menu';
import { useAppSelector } from 'store';
import { selectIsDropshipSetup, selectIsSeller } from 'store/selectors/me/company';
import { listTradeOpsIssues } from 'utils/api/issues';
import Collapse, { CollapseChevron } from 'storybookConfig/stories/molecules/Collapse';
import { SectionHeading, SubLinkContainer } from './Shared.styles';
import Badge from 'storybookConfig/stories/molecules/Badge';
import useFlagEnabled from 'hooks/useFlagEnabled';

const FETCHING_TRADE_OPS_ISSUES_QUERY_KEY = [
  'listTradeOpsIssues',
  {
    page: 0,
    limit: 100,
    archived: false,
    severityLevels: ['error', 'critical'],
  },
] as const;

interface DropshipLinksProps {
  isActive: (path: string) => boolean;
  partnerCount: number;
}

const getPartnersMenuLabel = (isSeller: boolean) => (isSeller ? 'Retailers' : 'Suppliers');

const DropshipLinks = ({ isActive, partnerCount }: DropshipLinksProps) => {
  const showMarketplace = useFlagEnabled('showMarketplace');
  const [expanded, setExpanded] = useState(true);
  const dropshipId = useId();
  const isSeller = useAppSelector(selectIsSeller);
  const partnersMenuLabel = getPartnersMenuLabel(isSeller);
  const isDropshipSetup = useAppSelector(selectIsDropshipSetup);

  const fetchingTradeOpsIssues = useQuery({
    queryKey: FETCHING_TRADE_OPS_ISSUES_QUERY_KEY,
    queryFn: ({ queryKey }) => listTradeOpsIssues(queryKey[1]),
    enabled: isDropshipSetup,
  });

  const tradeOpsIssues = fetchingTradeOpsIssues.data?.data?.errors ?? [];

  const issuesLink = isDropshipSetup && (
    <Menu.Link to="/issues" $iconName="circle-alert" $isActive={isActive('/issues')}>
      Issues
      <Badge variant="info" count={tradeOpsIssues.length} data-testid="issue-count-badge" />
    </Menu.Link>
  );

  return (
    <>
      <Menu.LinkButton
        aria-expanded={expanded}
        aria-controls={dropshipId}
        onClick={() => {
          setExpanded(!expanded);
        }}
      >
        <SectionHeading>Dropship</SectionHeading>
        <CollapseChevron open={expanded} />
      </Menu.LinkButton>
      <Collapse id={dropshipId} open={expanded}>
        <SubLinkContainer>
          <Menu.Link $iconName="download" to="/orders" $isActive={isActive('/orders')}>
            Orders
          </Menu.Link>
          <Menu.Link $iconName="receipt-text" to="/invoices" $isActive={isActive('/invoices')}>
            Invoices
          </Menu.Link>
          <Menu.Link $iconName="blocks" to="/products" $isActive={isActive('/products')}>
            Products
          </Menu.Link>
          {isSeller && (
            <Menu.Link $iconName="receipt" to="/prices" $isActive={isActive('/prices')}>
              Price Lists
            </Menu.Link>
          )}
          <Menu.Link to="/partners" $iconName="users" $isActive={isActive('/partners')}>
            {partnersMenuLabel}
            <Badge variant="info" count={partnerCount} data-testid="partner-count-badge" />
          </Menu.Link>
          {issuesLink}
          {showMarketplace && (
            <Menu.Link
              to="/requests/active"
              $iconName="mail"
              $isActive={isActive('/requests/:type')}
            >
              Requests
              <Badge variant="info" count={4} data-testid="partner-count-badge" />
            </Menu.Link>
          )}
        </SubLinkContainer>
      </Collapse>
    </>
  );
};

export default DropshipLinks;
